import { useState, Fragment } from "react";
import "../../crawler/crawler.css";
import toast from "react-hot-toast";
import moment from "moment";
import SwitchButton from "../../../components/commons/SwitchBtn";
import mainAxios from "../../../redux/api/mainAxios";
import Dot from "../../backOffice/components/Dot";

const CrawlerBody = ({ crawler, setCrawler, search, setSearch }) => {
  const [syncLoading, setSyncLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);

  const handleToggle = (itemId, newStatus) => {
    setCrawler((prevCrawler) => ({
      ...prevCrawler,
      data: prevCrawler.data.map((item) =>
        item.documentId === itemId ? { ...item, actif: newStatus } : item,
      ),
    }));
  };

  const syncGridkyData = async (documentId) => {
    setSyncLoading(true);
    try {
      toast.success("Démarrage de la synchronisation des données");
      // Capture the current time
      const currentTime = moment().toISOString();

      // Update the lastSync field
      const response = await mainAxios.put(`api/api-listes/${documentId}`, {
        data: {
          lastSync: currentTime,
        },
      });
      if (response.data?.data?.name === "ImmoLead") {
        await mainAxios.get(`/api/sync-immo-lead`);
      } else {
        await mainAxios.get(`/api/sync-gridky-data`);
      }

      setSyncLoading(false);
      toast.success("Synchronisation des données terminée");
    } catch (error) {
      console.error("Error syncing data:", error);
      toast.error("Erreur lors de la synchronisation des données");
      setSyncLoading(false);
    }
  };

  const handleDropdown = async (documentId) => {
    // If the dropdown is already open for this documentId, just close it
    if (dropdownOpen === documentId) {
      setDropdownOpen(null);
      return;
    }

    try {
      const response = await mainAxios.post(`/api/get-crawler-log-history`, {
        data: {
          crawlerId: documentId,
        },
      });
      if (response.data.data) {
        setSyncHistory(response.data.data.history);
      }
      setDropdownOpen(documentId);
    } catch (error) {
      console.error("Error opening dropdown", error);
      toast.error("Erreur lors de l'ouverture du dropdown");
    }
  };

  const handleDownloadLogs = async (crawlerId, historyId) => {
    try {
      toast.loading("Téléchargement des journaux en cours...");

      // Use axios to make the request but configure it for file download
      const response = await mainAxios.post(
        `/api/download-sync-logs/`,
        {
          data: {
            crawlerId,
            historyId,
          },
        },
        {
          responseType: "blob", // Important for file downloads
        },
      );

      // Create a blob URL from the response data
      const blob = new Blob([response.data], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);

      // Extract filename from headers if available or create a default name
      const contentDisposition = response.headers["content-disposition"];
      let filename = `sync_logs_${historyId}.txt`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create and trigger download link
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);

      toast.dismiss();
      toast.success("Téléchargement terminé");
    } catch (error) {
      console.error("Error downloading logs", error);
      toast.dismiss();
      toast.error("Erreur lors du téléchargement des journaux");
    }
  };

  const filteredCrawlerList = crawler?.data?.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      {/* Header */}
      <div className="crawler-table-header pink text-center">
        {[
          "Nom",
          "Activation",
          "État",
          "Création",
          "Dernière mise à jour",
          "Actions",
        ].map((header, index) => (
          <div key={index} className="table-cell pink semibold">
            {header}
          </div>
        ))}
      </div>
      {/* Body */}
      <div className="crawler-table-body">
        {filteredCrawlerList && filteredCrawlerList.length > 0 ? (
          <Fragment>
            {filteredCrawlerList.map((item, index) => (
              <Fragment key={`crawler-${item.documentId || index}`}>
                <div className="crawler-row">
                  <div className="text-center semibold fs16 darkGrey">
                    {item.name}
                  </div>
                  <SwitchButton
                    isActive={item.actif}
                    itemId={item.documentId}
                    fieldName="actif"
                    onToggle={(newStatus) =>
                      handleToggle(item.documentId, newStatus)
                    }
                  />
                  <div className="text-center semibold fs16 darkGrey text-truncate mw-100">
                    {item.health === "ok" ? (
                      <div className="d-flex align-items-center gap-2">
                        <Dot color="#4caf50" /> Fonctionnel
                      </div>
                    ) : item.health === "erreur" ? (
                      <div className="d-flex align-items-center gap-2">
                        <Dot color="rgb(220, 38, 38)" /> Erreur
                      </div>
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        <Dot color="rgb(220, 38, 38)" /> Erreur Partielle
                      </div>
                    )}
                  </div>
                  <div className="text-center semibold fs16 darkGrey">
                    {moment(item.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                  </div>
                  <div className="text-center semibold fs16 darkGrey">
                    {moment(item.lastSync).format("DD/MM/YYYY HH:mm:ss")}
                  </div>
                  <div className="table-cell">
                    <div className="d-flex align-items-center gap-1">
                      <button
                        className={
                          item.actif && !syncLoading && !item.sync_Ongoing
                            ? "radius5 px-20 py-10 bg-pink text-white border-0"
                            : "radius5 px-20 py-10 bg-soft-grey text-white border-0 pointer-none"
                        }
                        disabled={
                          !item.actif ||
                          syncLoading ||
                          item.sync_Ongoing === true
                        }
                        onClick={() => syncGridkyData(item.documentId)}
                      >
                        Synchroniser
                      </button>
                      <button
                        className="arrow-btn radius5 bg-pink text-white border-0 ml-1"
                        onClick={() => handleDropdown(item.documentId)}
                      >
                        <span>▼</span>
                      </button>
                    </div>
                  </div>
                </div>
                {dropdownOpen === item.documentId && (
                  <div className="crawler-logs-row">
                    <div className="logs-content">
                      {syncHistory && syncHistory.length > 0 ? (
                        <Fragment>
                          <div className="history-header">
                            <div className="history-cell">Date de début</div>
                            <div className="history-cell">Date de fin</div>
                            <div className="history-cell">Statut</div>
                            <div className="history-cell">Sync. auto</div>
                            <div className="history-cell">Actions</div>
                          </div>
                          {syncHistory.map((history, historyIndex) => (
                            <div
                              key={`history-${history.id || historyIndex}`}
                              className="history-row"
                            >
                              <div className="history-cell">
                                {moment(history.createdAt).format(
                                  "DD/MM/YYYY HH:mm:ss",
                                )}
                              </div>
                              <div className="history-cell">
                                {history.endDate
                                  ? moment(history.endDate).format(
                                      "DD/MM/YYYY HH:mm:ss",
                                    )
                                  : "-"}
                              </div>
                              <div className="history-cell">
                                <span
                                  className={`status-badge ${history.statusSync.toLowerCase().replace(/\s/g, "-")}`}
                                >
                                  {history.statusSync}
                                </span>
                              </div>
                              <div className="history-cell">
                                {history.automaticSync ? "Oui" : "Non"}
                              </div>
                              <div className="history-cell">
                                <button
                                  className="radius5 px-20 py-10 bg-pink text-white border-0"
                                  onClick={() => {
                                    handleDownloadLogs(item.id, history.id);
                                  }}
                                >
                                  Télécharger les journaux
                                </button>
                              </div>
                            </div>
                          ))}
                        </Fragment>
                      ) : (
                        <Fragment>
                          <div className="history-header">
                            <div className="history-cell">Date de début</div>
                            <div className="history-cell">Date de fin</div>
                            <div className="history-cell">Statut</div>
                            <div className="history-cell">Sync. auto</div>
                            <div className="history-cell">Actions</div>
                          </div>
                          <div className="history-row">
                            <div className="no-logs">Pas de journaux</div>
                          </div>
                        </Fragment>
                      )}
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
          </Fragment>
        ) : (
          <div className="d-flex justify-content-center align-items-center">
            Pas de crawlers
          </div>
        )}
      </div>
    </div>
  );
};

export default CrawlerBody;
